import * as React from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { cn } from "../../lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./dialog";
import { Input } from "./input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./select";
import { Button } from "./button";
import { Label, Text, Caption } from "./typography";
import { API_BASE_URL } from '../../lib/api';

/**
 * AddHoldingDialog - Dialog for adding a new holding to the portfolio
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onOpenChange - Callback when open state changes
 * @param {Function} props.onSuccess - Callback when holding is created successfully
 * @param {string} props.defaultAssetType - Optional default asset type to pre-select
 */
export function AddHoldingDialog({
  open,
  onOpenChange,
  onSuccess,
  defaultAssetType = "Stock",
}) {
  const [formData, setFormData] = React.useState({
    assetName: "",
    ticker: "",
    assetType: defaultAssetType,
    units: "",
    purchasePrice: "",
    currency: "EUR",
    platform: "",
    dateAcquired: new Date().toISOString().split("T")[0],
    notes: "",
    // Manual asset fields
    useManualValue: false,
    manualCurrentValue: "",
    isLiability: false,
  });

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [fetchingRate, setFetchingRate] = React.useState(false);
  const [exchangeRate, setExchangeRate] = React.useState("");

  // Asset type definitions
  const INVESTMENT_TYPES = ["Stock", "ETF", "Cryptocurrency", "Precious Metal"];
  const MANUAL_ASSET_TYPES = ["Cash/Bank Account", "Real Estate & Property", "Vehicle", "Equipment", "Other High-Value Asset"];
  const LIABILITY_TYPES = ["Mortgage", "Loan", "Credit Card Debt", "Other Liability"];
  const ALL_ASSET_TYPES = [...INVESTMENT_TYPES, ...MANUAL_ASSET_TYPES, ...LIABILITY_TYPES];

  const isManualAsset = [...MANUAL_ASSET_TYPES, ...LIABILITY_TYPES].includes(formData.assetType);
  const isLiability = LIABILITY_TYPES.includes(formData.assetType);

  const currencies = [
    { value: "EUR", label: "EUR - Euro" },
    { value: "USD", label: "USD - US Dollar" },
    { value: "GBP", label: "GBP - British Pound" },
    { value: "GBp", label: "GBp - British Pence" },
    { value: "SEK", label: "SEK - Swedish Krona" },
    { value: "NOK", label: "NOK - Norwegian Krone" },
    { value: "HKD", label: "HKD - Hong Kong Dollar" },
  ];

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      setFormData({
        assetName: "",
        ticker: "",
        assetType: defaultAssetType,
        units: "",
        purchasePrice: "",
        currency: "EUR",
        platform: "",
        dateAcquired: new Date().toISOString().split("T")[0],
        notes: "",
        useManualValue: false,
        manualCurrentValue: "",
        isLiability: false,
      });
      setError(null);
      setExchangeRate("");
    }
  }, [open, defaultAssetType]);

  // Fetch historical exchange rate when date or currency changes
  React.useEffect(() => {
    const fetchHistoricalRate = async () => {
      if (formData.currency === "EUR") {
        setExchangeRate("1.0");
        return;
      }

      if (!formData.dateAcquired || isManualAsset) {
        return;
      }

      setFetchingRate(true);
      try {
        const response = await axios.get(
          `https://api.frankfurter.app/${formData.dateAcquired}`,
          {
            params: {
              from: formData.currency,
              to: "EUR",
            },
          }
        );

        if (response.data?.rates?.EUR) {
          setExchangeRate(response.data.rates.EUR.toFixed(4));
        }
      } catch (err) {
        console.warn("Could not fetch historical rate:", err.message);
        // Try latest rate as fallback
        try {
          const fallbackResponse = await axios.get(
            "https://api.frankfurter.app/latest",
            {
              params: {
                from: formData.currency,
                to: "EUR",
              },
            }
          );
          if (fallbackResponse.data?.rates?.EUR) {
            setExchangeRate(fallbackResponse.data.rates.EUR.toFixed(4));
          }
        } catch (fallbackErr) {
          console.error("Fallback rate fetch failed:", fallbackErr.message);
        }
      } finally {
        setFetchingRate(false);
      }
    };

    fetchHistoricalRate();
  }, [formData.dateAcquired, formData.currency, isManualAsset]);

  // Handle asset type change
  const handleAssetTypeChange = (value) => {
    const isManual = [...MANUAL_ASSET_TYPES, ...LIABILITY_TYPES].includes(value);
    const isLiab = LIABILITY_TYPES.includes(value);

    setFormData((prev) => ({
      ...prev,
      assetType: value,
      useManualValue: isManual,
      isLiability: isLiab,
      // Clear ticker for manual assets
      ...(isManual ? { ticker: "", units: "1" } : {}),
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.assetName.trim()) {
      return "Asset name is required";
    }
    if (!isManualAsset && !formData.ticker.trim()) {
      return "Ticker symbol is required for investment assets";
    }
    if (!formData.units || parseFloat(formData.units) <= 0) {
      return "Units must be a positive number";
    }
    if (!formData.purchasePrice || parseFloat(formData.purchasePrice) < 0) {
      return "Purchase price must be a non-negative number";
    }
    if (isManualAsset && (!formData.manualCurrentValue || parseFloat(formData.manualCurrentValue) < 0)) {
      return "Current value is required for manual assets";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        purchaseExchangeRate: exchangeRate || undefined,
      };

      // Ensure manual assets have required fields
      if (isManualAsset) {
        payload.useManualValue = true;
        payload.lastManualUpdate = new Date().toISOString();
      }

      const response = await axios.post(`${API_BASE_URL}/holdings`, payload, {
        withCredentials: true,
      });

      // Call success callback and close dialog
      if (onSuccess) {
        onSuccess(response.data);
      }
      onOpenChange(false);
    } catch (err) {
      let errorMsg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.message;

      // Handle case where error is an object
      if (typeof errorMsg === "object" && errorMsg !== null) {
        errorMsg = errorMsg.message || JSON.stringify(errorMsg);
      }

      console.error("Failed to create holding:", err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add New Holding</DialogTitle>
          <DialogDescription>
            Add a new asset to your portfolio
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto flex-1 px-4 sm:px-0">
          {/* Error Message */}
          {error && (
            <div className="rounded-md bg-destructive/10 border border-destructive p-3">
              <Text variant="error" size="sm">
                {error}
              </Text>
            </div>
          )}

          {/* ===== ASSET DETAILS SECTION ===== */}
          <fieldset className="space-y-3 rounded-lg border border-border/50 p-4">
            <legend className="px-2 text-sm font-medium text-muted-foreground">
              Asset Details
            </legend>

            {/* Asset Type - Full Width */}
            <div className="space-y-1.5">
              <Label className="text-foreground text-sm">Asset Type *</Label>
              <Select
                value={formData.assetType}
                onValueChange={(value) => handleAssetTypeChange(value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select asset type" />
                </SelectTrigger>
                <SelectContent>
                  {ALL_ASSET_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isManualAsset && (
                <Caption className={cn(
                  "block",
                  isLiability ? "text-destructive" : "text-[var(--color-state-info-text)]"
                )}>
                  {isLiability
                    ? "This liability will reduce your net worth."
                    : "This asset requires manual value updates."}
                </Caption>
              )}
            </div>

            {/* Name and Ticker - responsive columns */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">
                  {isLiability ? "Description *" : "Asset Name *"}
                </Label>
                <Input
                  name="assetName"
                  value={formData.assetName}
                  onChange={handleInputChange}
                  placeholder={
                    isLiability
                      ? "e.g., Home Mortgage"
                      : formData.assetType === "Cash/Bank Account"
                      ? "e.g., Chase Checking"
                      : "e.g., Apple Inc."
                  }
                  required
                />
              </div>

              {!isManualAsset ? (
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">
                    {formData.assetType === "Cryptocurrency" ? "Symbol *" : "Ticker *"}
                  </Label>
                  <Input
                    name="ticker"
                    value={formData.ticker}
                    onChange={handleInputChange}
                    placeholder={formData.assetType === "Cryptocurrency" ? "e.g., BTC" : "e.g., AAPL"}
                    required
                  />
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">
                    {isLiability ? "Date Originated" : "Date Acquired"}
                  </Label>
                  <Input
                    name="dateAcquired"
                    type="date"
                    value={formData.dateAcquired}
                    onChange={handleInputChange}
                  />
                </div>
              )}
            </div>
          </fieldset>

          {/* ===== FINANCIAL INFORMATION SECTION ===== */}
          <fieldset className="space-y-3 rounded-lg border border-border/50 p-4">
            <legend className="px-2 text-sm font-medium text-muted-foreground">
              Financial Information
            </legend>

            {/* Units, Price, Currency - responsive columns */}
            <div className={cn("grid gap-3 grid-cols-1", isManualAsset ? "sm:grid-cols-2" : "sm:grid-cols-3")}>
              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">
                  {isManualAsset ? "Units" : "Units *"}
                </Label>
                <Input
                  name="units"
                  type="number"
                  step="0.0001"
                  value={formData.units}
                  onChange={handleInputChange}
                  placeholder={isManualAsset ? "1" : "e.g., 10.5"}
                  required={!isManualAsset}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">
                  {isLiability ? "Original Amount *" : "Purchase Price *"}
                </Label>
                <Input
                  name="purchasePrice"
                  type="number"
                  step="0.01"
                  value={formData.purchasePrice}
                  onChange={handleInputChange}
                  placeholder={isLiability ? "e.g., 400000" : "e.g., 150.00"}
                  required
                />
              </div>

              {!isManualAsset && (
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Currency *</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) => handleSelectChange("currency", value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Current Value for manual assets */}
            {isManualAsset && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">
                    {isLiability ? "Current Balance *" : "Current Value *"}
                  </Label>
                  <Input
                    name="manualCurrentValue"
                    type="number"
                    step="0.01"
                    value={formData.manualCurrentValue}
                    onChange={handleInputChange}
                    placeholder={isLiability ? "e.g., 250000" : "e.g., 650000"}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Currency *</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) => handleSelectChange("currency", value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Exchange Rate - for non-EUR investments */}
            {!isManualAsset && formData.currency !== "EUR" && (
              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">
                  Exchange Rate (1 {formData.currency} = ? EUR)
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={exchangeRate}
                    onChange={(e) => setExchangeRate(e.target.value)}
                    placeholder="e.g., 0.9600"
                    disabled={fetchingRate}
                    className="max-w-[200px]"
                  />
                  {fetchingRate && (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                  <Caption className="text-xs">Auto-filled, editable</Caption>
                </div>
              </div>
            )}
          </fieldset>

          {/* ===== ADDITIONAL DETAILS SECTION ===== */}
          <fieldset className="space-y-3 rounded-lg border border-border/50 p-4">
            <legend className="px-2 text-sm font-medium text-muted-foreground">
              Additional Details
            </legend>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {!isManualAsset && (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-foreground text-sm">
                      {formData.assetType === "Cryptocurrency" ? "Exchange/Wallet" : "Platform/Broker"}
                    </Label>
                    <Input
                      name="platform"
                      value={formData.platform}
                      onChange={handleInputChange}
                      placeholder={
                        formData.assetType === "Cryptocurrency"
                          ? "e.g., Coinbase"
                          : "e.g., Interactive Brokers"
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-foreground text-sm">Purchase Date</Label>
                    <Input
                      name="dateAcquired"
                      type="date"
                      value={formData.dateAcquired}
                      onChange={handleInputChange}
                    />
                  </div>
                </>
              )}

              <div className={cn("space-y-1.5", "sm:col-span-2")}>
                <Label className="text-foreground text-sm">Notes</Label>
                <Input
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Optional notes..."
                />
              </div>
            </div>
          </fieldset>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="w-full sm:w-auto">
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                "Add Holding"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AddHoldingDialog;
